import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CommissionBlock } from "@/components/commissions/CommissionBlock";
import Layout from "@/components/Layout";
import StarRating from "@/components/StarRating";
import DossierTimeline from "@/components/DossierTimeline";
import { isDAv2 } from "@/lib/da";
import BosBlock from "@/components/BosBlock";
import BanquePill from "@/components/BanquePill";
import RelancesBlock from "@/components/RelancesBlock";
import LostDialog from "@/components/LostDialog";
import QuickNoteDialog from "@/components/QuickNoteDialog";
import StandbyDialog from "@/components/StandbyDialog";
import ScoringV8ShadowPanel from "@/components/ScoringV8ShadowPanel";
import { AcquisitionBlock } from "@/components/AcquisitionBlock";
import { useConfirm } from "@/components/useConfirm";
import { api, CONTACT_STATUS_OPTIONS, PROJECT_MATURITY_OPTIONS, PART_OPTIONS, BANQUES_SUGGESTIONS, formatEUR, formatEURCompact, getStatusPill, relanceLabel } from "@/lib/api";
import { track, USAGE_EVENTS } from "@/lib/usage";
import {
  ArrowLeft, Flame, X, Trash, Warning, PencilSimple, TrendUp, House, Phone, Envelope, ArrowRight, GitBranch, CheckCircle, NotePencil, ArrowClockwise, Buildings, User, Plus, Bell, FileText, Moon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { MarketplacePublishBlock } from "@/components/MarketplacePublishBlock";
import { WeeklyFollowupBlock } from "@/components/WeeklyFollowupBlock";

function Pill({ pill }) {
  if (!pill) return null;
  return <span className={`pill pill-${pill.color} uppercase text-[11px] tracking-wider`}>{pill.label}</span>;
}

// V4.30 — Champ téléphone client édition inline + validation soft (FR).
// Accepte 06… 07… +33… avec espaces, points, tirets. Nullable. Warning discret
// si le format semble inhabituel, mais sauvegarde toujours (non bloquant).
function normalizePhoneDigits(s) {
  return String(s || "").replace(/[\s.\-()]/g, "");
}
function isLikelyFrPhone(s) {
  const v = normalizePhoneDigits(s);
  if (!v) return true; // vide = OK (nullable)
  // 0X XXXXXXXX (10 chiffres) OU +33XXXXXXXXX OU 33XXXXXXXXX OU international E.164 (+XX…, 8 à 15 chiffres)
  return /^(?:\+?33|0)[1-9]\d{8}$/.test(v) || /^\+[1-9]\d{7,14}$/.test(v);
}
function PhoneInline({ value, onSave, display }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  const trimmed = (local || "").trim();
  const looksOk = isLikelyFrPhone(trimmed);
  const showWarning = trimmed.length > 0 && !looksOk;
  const digits = normalizePhoneDigits(trimmed);
  const hasUsable = digits.length >= 8;
  const tel = trimmed ? `tel:${digits}` : null;
  const sms = trimmed ? `sms:${digits}` : null;
  const wa = trimmed ? `https://wa.me/${digits.replace(/^\+/, "").replace(/^0/, "33")}` : null;
  return (
    <div className="mb-4 pb-4 border-b border-slate-100" data-testid="fiche-client-phone-block">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Téléphone</div>
      <div className="flex items-center gap-2">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={local}
          placeholder="06 12 34 56 78"
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            const v = (local || "").trim() || null;
            onSave(v);
          }}
          className="cf-input text-sm py-2 flex-1"
          data-testid="fiche-client-phone-input"
        />
        {hasUsable && (
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={tel}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[color:var(--cf-primary)] text-white hover:bg-[color:var(--cf-primary)] transition-colors"
              title="Appeler"
              data-testid="fiche-client-phone-call"
            >
              <Phone size={16} weight="fill" />
            </a>
            <a
              href={sms}
              className="inline-flex items-center justify-center h-9 px-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors text-[11px] font-bold"
              title="SMS"
              data-testid="fiche-client-phone-sms"
            >
              SMS
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-9 px-2 rounded-lg bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors text-[11px] font-bold"
              title="WhatsApp"
              data-testid="fiche-client-phone-wa"
            >
              WA
            </a>
          </div>
        )}
      </div>
      {display && display !== trimmed && (
        <div className="mt-1 text-[11px] text-slate-500" data-testid="fiche-client-phone-display">Affichage : <span className="font-bold text-slate-700">{display}</span> · stocké au format international</div>
      )}
      {showWarning && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-600" data-testid="fiche-client-phone-warning">
          <Warning size={12} weight="fill" />
          <span>Format inhabituel — sauvegardé quand même</span>
        </div>
      )}
    </div>
  );
}

// V4.35 — Champ email client édition inline + validation soft.
// Nullable. Warning discret si format inhabituel, mais sauvegarde toujours (non bloquant).
// Sert à préremplir le destinataire des brouillons Gmail (templates client).
function isLikelyEmail(s) {
  const v = (s || "").trim();
  if (!v) return true; // vide = OK (nullable)
  // Validation très permissive : un @ avec au moins un . après
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function EmailInline({ value, onSave }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  const trimmed = (local || "").trim();
  const looksOk = isLikelyEmail(trimmed);
  const showWarning = trimmed.length > 0 && !looksOk;
  const mailto = trimmed && looksOk ? `mailto:${trimmed}` : null;
  return (
    <div className="mb-4 pb-4 border-b border-slate-100" data-testid="fiche-client-email-block">
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Email</div>
      <div className="flex items-center gap-2">
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={local}
          placeholder="prenom.nom@example.com"
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            const v = (local || "").trim() || null;
            onSave(v);
          }}
          className="cf-input text-sm py-2 flex-1"
          data-testid="fiche-client-email-input"
        />
        {mailto && (
          <a
            href={mailto}
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-[color:var(--cf-primary)] text-white hover:bg-[color:var(--cf-primary)] transition-colors"
            title="Email"
            data-testid="fiche-client-email-mailto"
          >
            <Envelope size={16} weight="fill" />
          </a>
        )}
      </div>
      {showWarning && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-600" data-testid="fiche-client-email-warning">
          <Warning size={12} weight="fill" />
          <span>Format inhabituel — sauvegardé quand même</span>
        </div>
      )}
    </div>
  );
}

export default function FicheDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  const [showLost, setShowLost] = useState(false);
  const [showStandby, setShowStandby] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [editingCA, setEditingCA] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = async () => {
    try {
      const res = await api.get(`/dossiers/${id}`);
      setD(res.data);
      setNameInput(res.data.nom);
    } catch (e) {
      toast.error("Dossier introuvable");
      navigate("/");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // V4.36 — Track ouverture fiche dossier
    if (id) track(USAGE_EVENTS.DOSSIER_OPEN, { dossier_id: id });
  }, [id]);

  const update = async (payload) => {
    try {
      const res = await api.patch(`/dossiers/${id}`, payload);
      // V4.36 — Track les modifications de champs sensibles
      if ("client_phone" in payload) track(USAGE_EVENTS.DOSSIER_PHONE_MODIF, { dossier_id: id, etape: res.data?.etape });
      if ("client_email" in payload) track(USAGE_EVENTS.DOSSIER_EMAIL_MODIF, { dossier_id: id, etape: res.data?.etape });
      if ("status" in payload) track(USAGE_EVENTS.DOSSIER_STATUS_CHANGE, { dossier_id: id, etape: res.data?.etape, meta: { status: payload.status } });
      if ("etape" in payload) track(USAGE_EVENTS.DOSSIER_ETAPE_CHANGE, { dossier_id: id, etape: payload.etape });
      setD(res.data);
      return res.data;
    } catch (e) { toast.error(e.response?.data?.detail || "Mise à jour impossible"); }
  };

  const saveName = async () => {
    if (!nameInput.trim()) return setEditingName(false);
    if (nameInput.trim() !== d.nom) await update({ nom: nameInput.trim() });
    setEditingName(false);
  };

  const setContact = (key) => update({ type_contact: key });
  // V4.23 — Setters distincts (les 2 dimensions sont indépendantes)
  const setContactStatus = (key) => update({ contact_status: key });
  const setProjectMaturity = (key) => {
    // Toggle off si déjà actif (3 valeurs exclusives + état "aucun" valide)
    const next = d.project_maturity === key ? null : key;
    update({ project_maturity: next });
  };
  const setStars = (n) => update({ etoiles: n });
  const setMontant = (v) => update({ montant_bien: Number(v) || 0 });
  const setPart = (v) => update({ taux_remuneration_pct: Number(v) });
  const setRetroApporteur = (v) => update({ retro_apporteur: Number(v) || 0 });
  const setRetroParrain = (v) => update({ retro_parrain: Number(v) || 0 });
  const setCA = (field, v) => update({ ca_brut: { ...d.ca_brut, [field]: Number(v) || 0 } });
  const setApporteur = (v) => update({ apporteur: v });
  const setBanques = (list) => update({ banques: list });
  const updateField = (key, value) => update({ [key]: value });

  const addBanque = async (name) => {
    const t = (name || "").trim();
    if (!t || (d.banques || []).includes(t)) return;
    await setBanques([...(d.banques || []), t]);
  };
  const removeBanque = async (name) => {
    await setBanques((d.banques || []).filter((b) => b !== name));
  };

  const markRelance = async () => {
    try {
      const res = await api.post(`/dossiers/${id}/relance`);
      setD(res.data);
      toast.success("Relancé — prochaine relance calculée");
    } catch { toast.error("Erreur"); }
  };

  const addNote = () => setShowNoteDialog(true);

  const removeNote = async (noteId) => {
    const ok = await confirm({
      title: "Supprimer cette note ?",
      description: "Action définitive, la note ne pourra pas être récupérée.",
      confirmLabel: "Supprimer",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/dossiers/${id}/notes/${noteId}`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const reactivate = async () => {
    try {
      const res = await api.post(`/dossiers/${id}/reactivate`);
      setD(res.data);
      toast.success("Dossier réactivé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const advance = async (etape) => {
    if (d.status === "perdu") return;
    try {
      const res = await api.post(`/dossiers/${id}/advance-stage`, { etape });
      setD(res.data);
      if (etape === "acte_authentique") toast.success("Dossier signé 🎉");
      else toast.success("Étape mise à jour");
    } catch (e) { toast.error("Erreur étape"); }
  };

  // V4.26 — Marquer le dossier comme signé (acte authentique / dossier finalisé).
  // Distinct du mandat signé (toggleMandat). Confirmation obligatoire.
  // Le backend (/sign) préserve la date d'acte si elle existe, sinon stamp today.
  const markSigned = async () => {
    if (d.status === "signe") return;
    const ok = await confirm({
      title: "Confirmer le passage du dossier en signé ?",
      description:
        "Le dossier sortira du pipeline actif et apparaîtra dans la liste 'Dossiers signés'. La date d'acte existante est conservée (sinon date du jour). Action réversible.",
      confirmLabel: "Marquer signé",
      tone: "primary",
    });
    if (!ok) return;
    try {
      const res = await api.post(`/dossiers/${id}/sign`);
      setD(res.data);
      toast.success("Dossier marqué signé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la signature");
    }
  };

  const toggleMandat = async () => {
    try {
      const res = await api.post(`/dossiers/${id}/mandat`);
      setD(res.data);
      toast.success(res.data.mandat_signe ? "Mandat signé ✅" : "Mandat retiré");
    } catch { toast.error("Erreur"); }
  };

  const toggleFocus = async () => {
    try {
      const res = await api.post(`/dossiers/${id}/toggle-focus`);
      setD(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible"); }
  };

  const markLost = async (reason) => {
    try {
      const res = await api.post(`/dossiers/${id}/mark-lost`, { reason });
      setD(res.data);
      setShowLost(false);
      toast.success("Dossier marqué perdu");
    } catch (e) { toast.error("Erreur"); }
  };

  // V4.19 — Réactivation d'un dossier annulé (perdu → en_cours)
  const reopenFromLost = async () => {
    const ok = await confirm({
      title: "Réactiver ce dossier ?",
      description:
        "Le dossier repassera en cours dans le pipeline actif. Toutes les dates et l'historique sont conservés. Le motif d'annulation reste archivé.",
      confirmLabel: "Réactiver",
      tone: "primary",
    });
    if (!ok) return;
    try {
      const res = await api.post(`/dossiers/${id}/reopen-from-lost`);
      setD(res.data);
      toast.success("Dossier réactivé — modifie librement la timeline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réactivation");
    }
  };

  const deleteDossier = async () => {
    const ok = await confirm({
      title: "Supprimer ce dossier ?",
      description: "Toutes les données associées (notes, banques, étapes) seront perdues. Action définitive.",
      confirmLabel: "Supprimer définitivement",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/dossiers/${id}`); toast.success("Dossier supprimé"); navigate("/"); }
    catch (e) { toast.error("Erreur"); }
  };

  const pill = useMemo(() => d ? getStatusPill(d) : null, [d]);

  if (loading || !d) {
    return <Layout title="Fiche"><div className="card-base h-96 animate-pulse" /></Layout>;
  }

  return (
    <Layout title="Détails du dossier" centerTitle>
      <button
        onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/"); }}
        className="hidden md:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 font-semibold"
        data-testid="desktop-back"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      {/* DA v2 — prochaine étape mise en avant (lecture seule : next_step V9-A + NBA existants) */}
      {isDAv2() && (d.next_step || d.nba?.label) && (
        <div className="card-base p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="da2-next-step">
          <div className="w-2 h-2 rounded-full" style={{ background: "var(--cf-primary)" }} />
          <div className="flex-1 min-w-[200px]">
            <div className="overline">Prochaine étape</div>
            <div className="text-sm font-semibold text-slate-900">
              {d.next_step ? `${d.next_step.label} · ${new Date(d.next_step.date + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} (${d.next_step.kind === "planifie" ? "planifié" : "prévisionnel"})` : d.nba?.label}
            </div>
            {d.next_step && d.nba?.label && <div className="text-xs text-slate-500">{d.nba.label}</div>}
          </div>
          <span className="pill pill-green" data-testid="da2-next-step-etape">{(d.etape || "prospect").replace(/_/g, " ")}</span>
        </div>
      )}

      {/* Header card: name + stars + status + contact type */}
      <div className="card-base p-5 md:p-6 mb-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => e.key === "Enter" && saveName()}
                autoFocus
                className="cf-input font-display text-2xl md:text-3xl font-extrabold"
                data-testid="name-input"
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                className="flex items-center gap-2 text-left group w-full"
                data-testid="name-edit-btn"
              >
                <h1 className="font-display text-[22px] md:text-3xl font-extrabold text-slate-900 tracking-tight break-words">
                  {d.nom}
                </h1>
                {d.callback_requested_at && !d.callback_handled_at && (
                  <span className="inline-flex items-center text-[11px] font-extrabold rounded-md px-2 py-1 bg-amber-100 text-amber-800 flex-shrink-0 mt-1" data-testid="fiche-callback-badge">📞 Demande à être rappelé</span>
                )}
                <PencilSimple size={16} className="text-slate-400 group-hover:text-[color:var(--cf-primary)] flex-shrink-0 mt-2" />
              </button>
            )}
            {(() => {
              const e1 = [d.client_1_prenom, d.client_1_nom].filter(Boolean).join(" ").trim();
              const e2 = [d.client_2_prenom, d.client_2_nom].filter(Boolean).join(" ").trim();
              const emprunteurs = [e1, e2].filter(Boolean).join(" · ");
              const fallback = d.client_name && d.client_name !== d.nom ? d.client_name : null;
              const label = emprunteurs || fallback;
              return label ? (
                <div className="text-sm text-slate-500 mt-0.5" data-testid="fiche-emprunteurs">{label}</div>
              ) : null;
            })()}
            {/* V4.28 — Badge BOS si dossier délégué */}
            {d.bos_delegated && (
              <span
                className="inline-flex items-center gap-1 mt-1.5 text-[10.5px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded bg-violet-100 text-violet-800"
                title={d.bos_name ? `Suivi BOS — ${d.bos_name}` : "Dossier délégué au BOS"}
                data-testid="bos-badge-header"
              >
                BOS
                {d.bos_name && <span className="text-violet-600 font-bold normal-case tracking-normal">· {d.bos_name}</span>}
              </span>
            )}
          </div>
          <div className="flex-shrink-0 pt-1">
            <StarRating value={d.etoiles} onChange={setStars} size={22} testid="fiche-stars" />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <Pill pill={pill} />
          <button
            onClick={toggleFocus}
            disabled={d.status !== "en_cours"}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
              d.is_focus
                ? "bg-[color:var(--cf-warning-soft)] text-[color:var(--cf-warning-text)]"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            } disabled:opacity-40`}
            data-testid="focus-toggle"
          >
            {d.is_focus ? <X size={12} weight="bold" /> : <Flame size={12} weight="fill" />}
            {d.is_focus ? "Retirer focus" : "Focus"}
          </button>
        </div>

        {d.is_standby && (
          <div
            className="mt-4 rounded-xl bg-violet-50 border border-violet-200 px-3.5 py-2.5 flex items-start gap-2.5 da-standby"
            data-testid="standby-banner"
          >
            <Moon size={16} weight="fill" color="var(--cf-accent)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-extrabold text-violet-900">Dossier en stand-by</div>
              <div className="text-[11.5px] text-violet-700 mt-0.5">
                {d.standby_reason && <span className="font-semibold">Motif : {d.standby_reason}</span>}
                {d.standby_reason && d.standby_until && <span className="mx-1.5">·</span>}
                {d.standby_until && (
                  <span>Reprise prévue : {new Date(d.standby_until + "T00:00:00").toLocaleDateString("fr-FR")}</span>
                )}
                {!d.standby_until && !d.standby_reason && <span>Sans date de reprise programmée</span>}
              </div>
            </div>
            <button
              onClick={reactivate}
              className="flex-shrink-0 text-[11.5px] font-bold text-violet-700 hover:text-violet-900 underline decoration-dotted underline-offset-2"
              data-testid="banner-reactivate"
            >
              Réactiver
            </button>
          </div>
        )}

        {/* V4.23 — Deux dimensions séparées : statut contact / avancement projet */}
        <div className="mt-4 space-y-3" data-testid="contact-and-maturity-blocks">
          {/* BLOC 1 — Statut contact (pilote les relances) */}
          <div>
            <div className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5">
              Statut contact
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="contact-status-group">
              {CONTACT_STATUS_OPTIONS.map((c) => {
                const active = d.contact_status === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setContactStatus(c.key)}
                    className={`py-2.5 px-1.5 rounded-xl text-[12.5px] font-bold leading-tight whitespace-nowrap transition-all ${
                      active
                        ? "bg-[color:var(--cf-primary)] text-white shadow-[0_4px_12px_rgba(45,110,255,0.25)]"
                        : "bg-[color:var(--cf-bg-2)] text-slate-500 hover:bg-slate-200"
                    }`}
                    data-testid={`contact-status-${c.key}`}
                    aria-pressed={active}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* BLOC 2 — Avancement projet (pilote la priorité commerciale) */}
          <div>
            <div className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center justify-between">
              <span>Avancement projet</span>
              {d.project_maturity && (
                <button
                  type="button"
                  onClick={() => update({ project_maturity: null })}
                  className="text-[10px] font-bold text-slate-400 hover:text-slate-600 normal-case tracking-normal"
                  data-testid="project-maturity-clear"
                >
                  Effacer
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="project-maturity-group">
              {PROJECT_MATURITY_OPTIONS.map((m) => {
                const active = d.project_maturity === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => setProjectMaturity(m.key)}
                    className={`py-2.5 px-1.5 rounded-xl text-[12.5px] font-bold leading-tight whitespace-nowrap transition-all ${
                      active
                        ? "bg-[color:var(--cf-accent)] text-white shadow-[0_4px_12px_rgba(124,58,237,0.25)]"
                        : "bg-[color:var(--cf-bg-2)] text-slate-500 hover:bg-slate-200"
                    }`}
                    data-testid={`project-maturity-${m.key}`}
                    aria-pressed={active}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mandat signé — engagement client (indépendant des étapes bancaires) */}
        <div className="mt-4 border-t border-slate-100 pt-4 flex items-center justify-between gap-3" data-testid="mandat-row">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${d.mandat_signe ? "bg-[color:var(--cf-success-soft)]" : "bg-slate-100"}`}>
              <FileText size={20} weight="fill" color={d.mandat_signe ? "var(--cf-success)" : "var(--cf-text-3)"} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-extrabold text-slate-900">Mandat signé</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {d.mandat_signe
                  ? `Signé le ${new Date(d.mandat_signe_date).toLocaleDateString("fr-FR")}`
                  : "Engagement client — indépendant de l'étape bancaire"}
              </div>
            </div>
          </div>
          <button
            onClick={toggleMandat}
            disabled={d.status === "perdu"}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-[12px] font-bold transition-colors disabled:opacity-40 ${
              d.mandat_signe
                ? "bg-[color:var(--cf-success)] text-white hover:bg-[color:var(--cf-success)]"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid="mandat-toggle"
          >
            {d.mandat_signe ? "Mandat actif" : "Marquer signé"}
          </button>
        </div>

        {/* V11 — État des pièces (saisie manuelle, jamais déduite) */}
        <div className="mt-3 border-t border-slate-100 pt-3 flex flex-wrap items-center justify-between gap-2" data-testid="pieces-row">
          <div>
            <div className="text-[13px] font-extrabold text-slate-900">État des pièces</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Pilote l'action Commando : obtenir les pièces / déposer en banque / vérifier</div>
          </div>
          <div className="flex gap-1" data-testid="pieces-etat-group">
            {[["non_renseigne", "Non renseigné"], ["incomplet", "Incomplet"], ["complet", "Complet"]].map(([v, l]) => {
              const cur = d.pieces_etat || "non_renseigne";
              return (
                <button key={v} onClick={() => update({ pieces_etat: v })} disabled={d.status === "perdu"}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-bold border transition-colors disabled:opacity-40 ${cur === v ? (v === "complet" ? "bg-[color:var(--cf-success-soft)] border-[color:var(--cf-success-border)] text-[color:var(--cf-success-text)]" : v === "incomplet" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-100 border-slate-200 text-slate-700") : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                  data-testid={`pieces-etat-${v}`} aria-pressed={cur === v}>{l}</button>
              );
            })}
          </div>
        </div>

        {d.status === "perdu" && d.lost_reason && (
          <div className="card-tinted-red mt-4 p-3 flex items-start gap-2">
            <Warning size={16} className="text-[color:var(--cf-danger)] mt-0.5 flex-shrink-0" weight="fill" />
            <div>
              <div className="text-[11px] font-bold text-[color:var(--cf-danger-text)] uppercase tracking-widest">Motif perdu</div>
              <div className="text-sm text-slate-700 mt-0.5">{d.lost_reason}</div>
            </div>
          </div>
        )}
        {/* V4.19 — Trace d'une réactivation (historique conservé) */}
        {d.status !== "perdu" && d.last_lost_reason && (
          <div
            className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2"
            data-testid="last-lost-reason-trace"
          >
            <ArrowClockwise size={14} className="text-slate-500 mt-0.5 flex-shrink-0" weight="bold" />
            <div>
              <div className="text-[10.5px] font-bold text-slate-500 uppercase tracking-widest">
                Réactivé{d.last_lost_at ? ` le ${d.last_lost_at}` : ""}
              </div>
              <div className="text-[12.5px] text-slate-600 mt-0.5 italic">
                Annulé précédemment — motif : « {d.last_lost_reason} »
              </div>
            </div>
          </div>
        )}
      </div>

      {d.status === "signe" && <CommissionBlock dossierId={id} refreshKey={d.encaissement_date} />}

      {/* Chiffre d'affaires */}
      <div className="card-base p-5 md:p-6 mb-4">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <TrendUp size={22} color="var(--cf-success)" weight="bold" />
            <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900">Chiffre d'Affaires</h2>
          </div>
          <button
            onClick={() => setEditingCA(!editingCA)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)] text-xs font-bold hover:bg-[color:var(--cf-primary-soft)]"
            data-testid="ca-edit-toggle"
          >
            <NotePencil size={14} weight="bold" />
            {editingCA ? "Fermer" : "Modifier"}
          </button>
        </div>

        {/* CAPP (CA BRUT) hero */}
        <div className="bg-[color:var(--cf-success-soft)] rounded-2xl p-5 text-center" data-testid="capp-hero">
          <div className="overline mb-1" style={{ color: "var(--cf-success-text)" }}>CAPP (CA BRUT)</div>
          <div className="metric-value text-4xl md:text-5xl text-[color:var(--cf-success)]">
            {formatEURCompact(d.ca_brut_total)}
          </div>
        </div>

        {/* Détail revenus */}
        <div className="mt-5">
          <div className="overline mb-3">Détail des revenus</div>
          <div className="space-y-3">
            {[
              { key: "honoraires", label: "Honoraires courtier", color: "var(--cf-primary)" },
              { key: "commission_banque", label: "Commission banque", color: "var(--cf-warning)" },
              { key: "assurance", label: "Assurance", color: "var(--cf-success)" },
            ].map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3 py-1.5">
                <div className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: f.color }} />
                  <span className="text-[15px] text-slate-700 font-medium">{f.label}</span>
                </div>
                {editingCA ? (
                  <input
                    type="number"
                    defaultValue={d.ca_brut[f.key]}
                    onBlur={(e) => {
                      if (Number(e.target.value) !== d.ca_brut[f.key]) setCA(f.key, e.target.value);
                    }}
                    className="cf-input w-32 text-right text-sm font-bold py-1.5"
                    data-testid={`ca-${f.key}`}
                  />
                ) : (
                  <span className="text-[15px] font-bold text-slate-900 tabular-nums">{formatEUR(d.ca_brut[f.key])}</span>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 mt-4 pt-4 flex items-center justify-between">
            <span className="font-display font-extrabold text-slate-900 text-base">Total CAPP</span>
            <span className="metric-value text-xl text-[color:var(--cf-success)]">{formatEUR(d.ca_brut_total)}</span>
          </div>

          {/* Taux de rémunération */}
          <div className="border-t border-slate-100 mt-4 pt-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <span className="text-sm text-slate-500 font-semibold">Taux de rémunération</span>
                <div className="text-[11px] text-slate-400 mt-0.5">% du CAPP gardé par le courtier</div>
              </div>
              <div className="segmented" data-testid="taux-remuneration">
                {PART_OPTIONS.map((p) => (
                  <button key={p} data-active={d.taux_remuneration_pct === p} onClick={() => setPart(p)} data-testid={`taux-remuneration-${p}`}>
                    {p}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CA Broker (après taux, avant rétrocessions) */}
          <div className="border-t border-slate-100 mt-4 pt-4 flex items-center justify-between">
            <div>
              <span className="text-sm text-slate-500 font-semibold">CA courtier</span>
              <div className="text-[11px] text-slate-400 mt-0.5">CAPP × {d.taux_remuneration_pct}%</div>
            </div>
            <span className="metric-value text-base text-slate-800" data-testid="ca-broker">{formatEUR(d.ca_broker)}</span>
          </div>

          {/* Rétrocessions */}
          <div className="border-t border-slate-100 mt-4 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Rétrocession apporteur</label>
              <input
                type="number" min="0" step="50"
                defaultValue={d.retro_apporteur || 0}
                onBlur={(e) => setRetroApporteur(e.target.value)}
                data-testid="retro-apporteur-input"
                className="input-base w-full"
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Rétrocession parrain</label>
              <input
                type="number" min="0" step="50"
                defaultValue={d.retro_parrain || 0}
                onBlur={(e) => setRetroParrain(e.target.value)}
                data-testid="retro-parrain-input"
                className="input-base w-full"
                placeholder="0"
              />
            </div>
          </div>

          {/* CA Net final */}
          <div className="border-t border-slate-100 mt-4 pt-4 bg-[color:var(--cf-primary-soft)] -mx-5 md:-mx-6 px-5 md:px-6 py-4 rounded-b-[20px]" data-testid="ca-net-row">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline" style={{ color: "var(--cf-primary-hover)" }}>CA Net (final)</div>
                <div className="text-[11px] text-slate-500 mt-0.5">CA courtier − rétrocessions</div>
              </div>
              <div className="metric-value text-3xl md:text-4xl text-[color:var(--cf-primary)]" data-testid="ca-net">
                {formatEUR(d.ca_net)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Avancement du dossier — Timeline 10 étapes (remplace StageProgress + CompromisBlock) */}
      <DossierTimeline dossier={d} onUpdated={(updated) => setD(updated)} />

      {/* V4.28 — Délégation BOS — V4.37 : éligibilité strictement à partir de l'offre éditée.
          Avant V4.37 : visible dès rdv_bancaire (confusion métier — BOS ≠ suivi bancaire).
          Maintenant : étape >= offre_editee OU etape_dates.offre_editee renseignée.
          Safe = Silence : si non éligible, le bloc disparaît complètement. */}
      {d.bos_eligible && (
        <BosBlock dossier={d} onUpdated={(updated) => setD(updated)} />
      )}

      <RelancesBlock dossier={d} onUpdated={(updated) => setD(updated)} />

      {/* Lot acquisition 1 — origine, jalons manuels, doublons signalés, consentement recontact */}
      <AcquisitionBlock dossier={d} onUpdated={(updated) => setD(updated)} />

      {/* V9-C — Marketplace : publication volontaire (fiche anonymisée) / retrait */}
      <MarketplacePublishBlock dossier={d} onUpdated={load} />

      {/* V9-D — Suivi hebdomadaire client (mandat prouvé, consentement, aperçu factuel) */}
      <WeeklyFollowupBlock dossier={d} onUpdated={load} />

      {/* === Informations Client V3 === */}
      <div className="card-base p-5 md:p-6 mb-4" data-testid="infos-client-card">
        <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900 mb-4">Informations client</h2>

        {/* V4.30 — Téléphone client éditable inline (débloque les CTAs Appeler / SMS / WhatsApp) */}
        <PhoneInline
          value={d.client_phone || ""}
          display={d.client_phone_display}
          onSave={(v) => {
            if ((v || null) !== (d.client_phone || null)) updateField("client_phone", v);
          }}
        />

        {/* V4.35 — Email client éditable inline (prérempli dans les brouillons Gmail) */}
        <EmailInline
          value={d.client_email || ""}
          onSave={(v) => {
            if ((v || null) !== (d.client_email || null)) updateField("client_email", v);
          }}
        />

        <div className="space-y-4">
          {[
            { idx: 1, label: "Emprunteur 1" },
            { idx: 2, label: "Emprunteur 2" },
          ].map(({ idx, label }) => {
            const prenomKey = `client_${idx}_prenom`;
            const nomKey = `client_${idx}_nom`;
            const dobKey = `client_${idx}_dob`;
            return (
              <div key={idx}>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{label}</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    defaultValue={d[prenomKey] || ""}
                    placeholder="Prénom"
                    onBlur={(e) => {
                      const v = e.target.value.trim() || null;
                      if (v !== (d[prenomKey] || null)) updateField(prenomKey, v);
                    }}
                    className="cf-input text-sm py-2"
                    data-testid={`fiche-c${idx}-prenom`}
                  />
                  <input
                    type="text"
                    defaultValue={d[nomKey] || ""}
                    placeholder="Nom"
                    onBlur={(e) => {
                      const v = e.target.value.trim() || null;
                      if (v !== (d[nomKey] || null)) updateField(nomKey, v);
                    }}
                    className="cf-input text-sm py-2"
                    data-testid={`fiche-c${idx}-nom`}
                  />
                </div>
                <input
                  type="date"
                  defaultValue={d[dobKey] || ""}
                  onChange={(e) => {
                    const v = e.target.value || null;
                    if (v !== (d[dobKey] || null)) updateField(dobKey, v);
                  }}
                  className="cf-input text-sm py-2 mt-2 w-full"
                  data-testid={`fiche-c${idx}-dob`}
                />
              </div>
            );
          })}
        </div>
        {/* Apporteur + commissions auto-fill V3 */}
        <div className="mt-5 pt-5 border-t border-slate-100 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Apporteur</span>
            <input
              defaultValue={d.apporteur || ""}
              placeholder="—"
              onBlur={(e) => { if ((e.target.value || "") !== (d.apporteur || "")) setApporteur(e.target.value); }}
              className="text-sm font-bold text-slate-900 text-right bg-transparent outline-none focus:bg-[color:var(--cf-bg-2)] rounded-md px-2 py-1 max-w-[180px]"
              data-testid="apporteur-input"
            />
          </div>
          {d.parrain && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Parrain</span>
              <span className="text-sm font-bold text-slate-900" data-testid="fiche-parrain">{d.parrain}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Commission apporteur</span>
            <input
              type="number"
              defaultValue={d.retro_apporteur || 0}
              onBlur={(e) => {
                const v = Number(e.target.value) || 0;
                if (v !== (d.retro_apporteur || 0)) updateField("retro_apporteur", v);
              }}
              className="text-sm font-bold text-slate-900 text-right bg-transparent outline-none focus:bg-[color:var(--cf-bg-2)] rounded-md px-2 py-1 w-24 tabular-nums"
              data-testid="fiche-retro-apporteur"
            />
          </div>
          {d.parrain && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Commission parrain</span>
              <input
                type="number"
                defaultValue={d.retro_parrain || 0}
                onBlur={(e) => {
                  const v = Number(e.target.value) || 0;
                  if (v !== (d.retro_parrain || 0)) updateField("retro_parrain", v);
                }}
                className="text-sm font-bold text-slate-900 text-right bg-transparent outline-none focus:bg-[color:var(--cf-bg-2)] rounded-md px-2 py-1 w-24 tabular-nums"
                data-testid="fiche-retro-parrain"
              />
            </div>
          )}
        </div>
      </div>

      {/* Banques sollicitées — pills cycliques 1-clic (Sollicitée → Retenue → Non retenue → Refus) */}
      <div className="card-base p-5 md:p-6 mb-4" data-testid="banques-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Buildings size={22} color="var(--cf-primary)" weight="fill" />
            <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900">Banques sollicitées</h2>
          </div>
          <span className="text-xs text-slate-500 font-bold">{(d.banques || []).length}</span>
        </div>
        {(d.banques || []).length === 0 ? (
          <p className="text-sm text-slate-400 mb-3">Aucune banque renseignée.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="banques-list">
            {d.banques.map((b) => (
              <BanquePill
                key={b}
                name={b}
                status={(d.banque_status || {})[b] || "neutral"}
                onChange={(newStatus) => {
                  const next = { ...(d.banque_status || {}), [b]: newStatus };
                  update({ banque_status: next });
                }}
                onRemove={() => removeBanque(b)}
              />
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            id="banque-add-input"
            className="cf-input flex-1"
            placeholder="BNP, Banque Populaire…"
            list="fiche-banques-suggest"
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addBanque(e.currentTarget.value); e.currentTarget.value = ""; }
            }}
            data-testid="banque-add-input"
          />
          <datalist id="fiche-banques-suggest">
            {BANQUES_SUGGESTIONS.map((b) => <option key={b} value={b} />)}
          </datalist>
          <button
            onClick={() => {
              const el = document.getElementById("banque-add-input");
              if (el?.value) { addBanque(el.value); el.value = ""; }
            }}
            className="px-3 rounded-xl bg-[color:var(--cf-primary)] text-white font-bold"
            data-testid="banque-add-btn"
          >
            <Plus size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Relance — bloc legacy retiré, remplacé par RelancesBlock V2 ci-dessus */}

      {/* Notes */}
      <div className="card-base p-5 md:p-6 mb-4" data-testid="notes-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <NotePencil size={22} color="var(--cf-primary)" weight="bold" />
            <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900">Notes</h2>
            <span className="text-xs text-slate-500 font-bold">{(d.notes || []).length}</span>
          </div>
          <button onClick={addNote} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)] text-xs font-bold hover:bg-[color:var(--cf-primary-soft)]" data-testid="note-add-btn">
            <Plus size={12} weight="bold" /> Ajouter
          </button>
        </div>
        {(d.notes || []).length === 0 ? (
          <p className="text-sm text-slate-400">Aucune note pour l'instant.</p>
        ) : (
          <ul className="space-y-2" data-testid="notes-list">
            {[...d.notes].reverse().map((n) => (
              <li key={n.id} className="bg-slate-50 rounded-xl p-3" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap flex-1">{n.content}</p>
                  <button onClick={() => removeNote(n.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`note-remove-${n.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
                <div className="text-[11px] text-slate-400 mt-1 font-mono-ticker">
                  {new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Marquer signé — CTA final (V4.26 : confirmation + endpoint dédié /sign) */}
      {d.status === "en_cours" && (
        <button
          onClick={markSigned}
          className="w-full mb-4 py-4 rounded-2xl bg-[color:var(--cf-success)] text-white font-extrabold text-base flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(16,185,129,0.3)] hover:bg-[color:var(--cf-success)] active:scale-[0.99]"
          data-testid="mark-signed-btn"
        >
          <CheckCircle size={20} weight="fill" /> Marquer signé
          <ArrowRight size={18} weight="bold" />
        </button>
      )}

      {/* V8 Shadow — observation admin, lecture seule, replié */}
      <ScoringV8ShadowPanel dossierId={id} />

      {/* Danger zone */}
      <div className="card-base p-5 md:p-6 mb-10">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline" style={{ color: "var(--cf-danger)" }}>Zone sensible</div>
            <div className="text-sm text-slate-500 mt-1">Mettre en stand-by, marquer perdu ou supprimer.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {d.is_standby ? (
              <button
                onClick={reactivate}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold bg-violet-100 text-violet-800 hover:bg-violet-200"
                data-testid="reactivate-btn"
              >
                <Moon size={14} weight="fill" /> Réactiver le dossier
              </button>
            ) : (
              d.status !== "perdu" && d.status !== "signe" && (
                <button
                  onClick={() => setShowStandby(true)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold bg-violet-50 text-violet-700 hover:bg-violet-100"
                  data-testid="standby-btn"
                >
                  <Moon size={14} /> Mettre en stand-by
                </button>
              )
            )}
            {d.status !== "perdu" && (
              <button
                onClick={() => setShowLost(true)}
                className="inline-flex items-center gap-2 bg-[color:var(--cf-danger-soft)] text-[color:var(--cf-danger-text)] hover:bg-[color:var(--cf-danger-border)] rounded-xl px-4 py-2 text-sm font-bold"
                data-testid="mark-lost-btn"
              >
                <Warning size={14} weight="fill" /> Marquer perdu
              </button>
            )}
            {/* V4.19 — Réactiver un dossier annulé */}
            {d.status === "perdu" && (
              <button
                onClick={reopenFromLost}
                className="inline-flex items-center gap-2 bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] hover:bg-[color:var(--cf-success-soft)] rounded-xl px-4 py-2 text-sm font-bold border border-[color:var(--cf-success-border)]"
                data-testid="reopen-from-lost-btn"
                title="Le dossier repasse en cours, dates et historique conservés"
              >
                <ArrowClockwise size={14} weight="bold" /> Réactiver le dossier
              </button>
            )}
            <button
              onClick={deleteDossier}
              className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl px-4 py-2 text-sm font-bold"
              data-testid="delete-btn"
            >
              <Trash size={14} /> Supprimer
            </button>
          </div>
        </div>
      </div>

      <LostDialog open={showLost} onClose={() => setShowLost(false)} onConfirm={markLost} />
      <StandbyDialog
        dossier={d}
        open={showStandby}
        onClose={() => setShowStandby(false)}
        onDone={(updated) => setD(updated)}
      />
      <QuickNoteDialog
        open={showNoteDialog}
        dossier={d}
        onClose={() => setShowNoteDialog(false)}
        onAdded={load}
      />
      {confirmDialog}
    </Layout>
  );
}
